import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { Form, Input } from "antd";
import { HiOutlineLocationMarker } from "react-icons/hi";
import FormLabel from "./FormLabel";
import OfficeLocationGuide from "./OfficeLocationGuide";
import PermissionHelpSteps from "./PermissionHelpSteps";
import { GEOLOCATION_OPTIONS } from "./GeoCameraModal";
import styles from "../BiformLayout.module.scss";
import { QuestionComponentProps, V2Question } from "../helper";
import { buildRules } from "../formUtils";
import { checkOfficeLocation } from "../apis";
import { isTurnedOffBySitePolicy, SITE_LOCATION_OFF_TITLE } from "../sitePermissionPolicy";
import {
  CURRENT_LOCATION_MAX_ACCURACY_METERS,
  findWeekZeroQuestion,
  isOfficeLocationInput,
  isOfficeMode,
  OFFICE_LOCATION_DESCRIPTION,
  OFFICE_NOT_FOUND_MESSAGE,
  officeLocationRules,
  WEEK_ZERO_MODE_MARKER
} from "../weekZeroLocation";

interface OfficeLocationQuestionProps extends QuestionComponentProps {
  /** Every question of the form, to find the one marked `internshipMode`. */
  questions: V2Question[];
}

const CHECK_DEBOUNCE_MS = 600;

/** "found" and "not_found" are core-api's answer; "error" is a failed request (Submit stays allowed). */
type CheckStatus = "found" | "not_found" | "error";

interface CheckResult {
  value: string;
  status: CheckStatus;
  mapUrl: string | null;
}

interface OfficeLocationInputProps {
  value?: string;
  /** antd's getValueFromEvent also accepts a plain value, which the current-location button uses. */
  onChange?: (value: string | React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  placeholder?: string;
  results: Map<string, CheckResult>;
  pending: Set<string>;
  onCheck: (value: string) => void;
}

const PERMISSION_DENIED_MESSAGE = "Location is blocked for this site. Allow it in your browser settings and try again, or paste the Google Maps link instead.";
// The site's own policy turns location off: no browser setting can change that, so no steps either.
const SITE_LOCATION_OFF_MESSAGE = `${SITE_LOCATION_OFF_TITLE}, so it can't be read here. Paste the Google Maps link instead.`;
const LOCATION_FAILED_MESSAGE = "Couldn't get your location. Try again, or paste the Google Maps link instead.";

type LocateState = "idle" | "locating" | "filled" | "error";
/** Why the last read failed: the browser refused it (steps can help), the site turns location off (they cannot), or anything else. */
type LocateFailure = "denied" | "siteOff" | "failed";

/** The input plus the live status line under it. antd injects value / onChange / onBlur. */
const OfficeLocationInput: FunctionComponent<OfficeLocationInputProps> = ({ value, onChange, onBlur, disabled, placeholder, results, pending, onCheck }) => {
  const text = (value || "").trim();
  const [canLocate, setCanLocate] = useState(false);
  const [locateState, setLocateState] = useState<LocateState>("idle");
  const [locateError, setLocateError] = useState<LocateFailure | null>(null);
  // The text the button put in the field, with the fix's accuracy; the notes under the status show while the field still holds it.
  const [filled, setFilled] = useState<{ value: string; accuracy: number } | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    setCanLocate(typeof navigator !== "undefined" && !!navigator.geolocation);
    return () => {
      mounted.current = false;
    };
  }, []);

  // An error is about the last attempt: editing the field dismisses it.
  useEffect(() => {
    setLocateError(null);
  }, [text]);

  const handleUseCurrentLocation = () => {
    setLocateState("locating");
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      position => {
        if (!mounted.current) return;
        const filledText = `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`;
        setFilled({ value: filledText, accuracy: position.coords.accuracy });
        setLocateState("filled");
        onChange?.(filledText);
        onCheck(filledText);
      },
      error => {
        if (!mounted.current) return;
        setLocateState("error");
        if (error?.code !== 1) setLocateError("failed");
        else setLocateError(isTurnedOffBySitePolicy("geolocation") ? "siteOff" : "denied");
      },
      GEOLOCATION_OPTIONS
    );
  };

  // Debounced check while typing; blur checks at once (the check is shared by value, so no duplicate request).
  useEffect(() => {
    if (disabled || !isOfficeLocationInput(text)) return undefined;
    const timer = setTimeout(() => onCheck(text), CHECK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text, disabled, onCheck]);

  // Results are per value: an older response can never stand in for the text now in the input.
  const current = results.get(text) ?? null;
  const showChecking = pending.has(text) && isOfficeLocationInput(text) && !current;

  return (
    <div>
      <Input
        value={value}
        disabled={disabled}
        placeholder={placeholder || "Enter"}
        onChange={onChange}
        onBlur={event => {
          onBlur?.(event);
          if (isOfficeLocationInput(event.target.value)) onCheck(event.target.value.trim());
        }}
      />
      {showChecking && <div className="mt-1 text-xs text-neutral-9">Checking the location…</div>}
      {current?.status === "found" && (
        <div className="mt-1 text-xs text-green-700">
          Office location found
          {current.mapUrl && (
            <>
              {" · "}
              <a href={current.mapUrl} target="_blank" rel="noopener noreferrer">
                Check it on the map
              </a>
            </>
          )}
        </div>
      )}
      {current?.status === "error" && <div className="mt-1 text-xs text-yellow-700">Couldn&apos;t check the link right now</div>}
      {!disabled && filled && text === filled.value && (
        <div className="mt-1">
          {filled.accuracy <= CURRENT_LOCATION_MAX_ACCURACY_METERS ? (
            <div className="text-xs text-neutral-8">From your current location, accurate to about {Math.round(filled.accuracy)} m.</div>
          ) : (
            <div className="rounded-lg bg-yellow-50 p-2 text-xs text-yellow-700">
              Your location is only accurate to about {Math.round(filled.accuracy)} m. Check the map, move near a window and try again, or paste the Google Maps
              link instead.
            </div>
          )}
        </div>
      )}
      {!disabled && locateError && (
        <div className="mt-1 flex flex-col gap-2 rounded-lg bg-red-50 p-2 text-xs text-red-700">
          <span>{locateError === "siteOff" ? SITE_LOCATION_OFF_MESSAGE : locateError === "denied" ? PERMISSION_DENIED_MESSAGE : LOCATION_FAILED_MESSAGE}</span>
          {locateError === "denied" && <PermissionHelpSteps locationOnly />}
        </div>
      )}
      {!disabled && canLocate && (
        <div className="mt-1">
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={locateState === "locating"}
            className="inline-flex min-h-[40px] items-center gap-1.5 py-2 text-left text-sm text-primary-6 hover:underline focus:outline-none focus-visible:underline disabled:cursor-wait disabled:no-underline"
          >
            <HiOutlineLocationMarker size={16} className={`flex-shrink-0${locateState === "locating" ? " animate-pulse" : ""}`} />
            {locateState === "locating"
              ? "Getting your location…"
              : locateState === "filled"
              ? "Use my current location again"
              : locateState === "error"
              ? "Try again"
              : "I'm at the office, use my current location"}
          </button>
          <div className="text-xs text-neutral-8">
            {locateState === "locating" ? "This can take up to 15 seconds." : "Only use this while you're inside your office building."}
          </div>
        </div>
      )}
      {!disabled && <OfficeLocationGuide />}
    </div>
  );
};

/**
 * Week 0 office location (a Google Maps link or coordinates). Mounted only while the mode question is Offline /
 * Hybrid; unmounted, antd does not validate it (the submit path also drops it and the layout clears its value).
 *
 * While the value is valid it is checked with core-api (debounced, and on blur). A value core-api cannot read
 * blocks Submit; a failed request does not (the server resolves it again and Send Geo Capture surfaces it).
 */
const OfficeLocationQuestion: FunctionComponent<OfficeLocationQuestionProps> = ({ question, questions, disabled = false }) => {
  const mode = findWeekZeroQuestion(questions, WEEK_ZERO_MODE_MARKER);
  const form = Form.useFormInstance();
  const [results, setResults] = useState<Map<string, CheckResult>>(() => new Map());
  const [pending, setPending] = useState<Set<string>>(() => new Set());
  // One request per value: the validator awaits the same promise the debounce / blur started.
  const checks = useRef(new Map<string, Promise<CheckResult>>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const runCheck = useCallback(
    (value: string): Promise<CheckResult> => {
      const existing = checks.current.get(value);
      if (existing) return existing;
      setPending(prev => new Set(prev).add(value));
      const request = checkOfficeLocation(value)
        .then((res): CheckResult => ({ value, status: res.valid && res.found ? "found" : "not_found", mapUrl: res.mapUrl }))
        .catch((): CheckResult => ({ value, status: "error", mapUrl: null }))
        .then(outcome => {
          // A failure is not remembered, so the next blur / keystroke tries again.
          if (outcome.status === "error") checks.current.delete(value);
          if (mounted.current) {
            setPending(prev => {
              const next = new Set(prev);
              next.delete(value);
              return next;
            });
            setResults(prev => new Map(prev).set(value, outcome));
            if (outcome.status === "not_found") form.validateFields([question.id]).catch(() => undefined);
          }
          return outcome;
        });
      checks.current.set(value, request);
      return request;
    },
    [form, question.id]
  );

  if (!mode) return null;
  return (
    <Form.Item noStyle shouldUpdate={(prev, next) => prev[mode.id] !== next[mode.id]}>
      {({ getFieldValue }) =>
        isOfficeMode(getFieldValue(mode.id)) ? (
          // The layout's wrapper is here, not around this question, so a hidden link leaves no empty gap behind.
          <div className={styles.formComponents}>
            <Form.Item
              name={question.id}
              label={<FormLabel label={question.label} required description={OFFICE_LOCATION_DESCRIPTION} />}
              rules={[
                ...officeLocationRules(),
                {
                  // Blocks Submit while the value is valid but core-api could not find the spot. Runs on blur and on
                  // submit only: while typing, the debounced check does the work (one request per value).
                  validateTrigger: ["onBlur"],
                  validator: async (_: unknown, value: unknown) => {
                    const text = typeof value === "string" ? value.trim() : "";
                    if (!text || !isOfficeLocationInput(text)) return;
                    const outcome = await runCheck(text);
                    if (outcome.status === "not_found") throw new Error(OFFICE_NOT_FOUND_MESSAGE);
                  }
                },
                ...buildRules({ ...question, isRequired: false })
              ]}
              validateTrigger={["onChange", "onBlur"]}
              className={styles.disableTextInput}
            >
              <OfficeLocationInput disabled={disabled} placeholder={question.placeholder || "Enter"} results={results} pending={pending} onCheck={runCheck} />
            </Form.Item>
          </div>
        ) : null
      }
    </Form.Item>
  );
};

export default OfficeLocationQuestion;
